import type { TOC } from '@ember/component/template-only';
import Heading from './heading';
import Text from './text';
import type { ComponentLike } from '@glint/template';
import { getIconTokens } from 'tio-reimagined/color-tokens';
import type { ReimaginedColor } from 'tio-reimagined/color-tokens';
import Card from './card';

// ---------------------------------------------------------------------------
// FeatureCard – icon + title + description card for feature/info grids
// ---------------------------------------------------------------------------

/**
 * Color theme options for the feature card icon
 */
export type FeatureCardColor = ReimaginedColor;

interface FeatureCardSignature {
  Args: {
    /** Phosphor icon component */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Title text */
    title: string;
    /** Description text */
    description?: string;
    /** Color theme for icon badge */
    color?: FeatureCardColor;
    /** Center layout (larger icon, centered text) — for contact/method cards */
    centered?: boolean;
  };
  Blocks: {
    /** Optional action content at the bottom (button, link, chip) */
    action?: [];
  };
  Element: HTMLDivElement;
}

const getColorClasses = (color?: FeatureCardColor) => getIconTokens(color);

/**
 * FeatureCard - An icon + title + description card for feature/info grids
 *
 * Used across dashboards for "optimization tips", "help resources", "how it works"
 * grids. Supports two layouts:
 * - **Default:** Horizontal icon badge + title row, description below
 * - **Centered (@centered):** Large centered icon, centered title/description, action block
 *
 * @example Default layout (tip/info grid)
 * ```gts
 * import FeatureCard from 'tio-reimagined/components/feature-card';
 *
 * <FeatureCard
 *   @icon={{PhRocket}}
 *   @title="Increase by 1%"
 *   @description="Increasing your contribution by just 1% could add $150,000+."
 *   @color="emerald"
 * />
 * ```
 *
 * @example Centered layout (contact/method card)
 * ```gts
 * <FeatureCard
 *   @icon={{PhEnvelope}}
 *   @title="Email Us"
 *   @description="Send us a message and we'll respond within 24 hours."
 *   @color="blue"
 *   @centered={{true}}
 * >
 *   <:action>
 *     <a href="mailto:support@tio.com" class="text-blue-600 font-medium text-sm">
 *       support@tio.com
 *     </a>
 *   </:action>
 * </FeatureCard>
 * ```
 */
const FeatureCard: TOC<FeatureCardSignature> = <template>
  {{#let (getColorClasses @color) as |colors|}}
    <Card
      data-test-feature-card
      @padding="lg"
      class="{{if @centered 'text-center hover:shadow-lg transition-shadow'}}"
      ...attributes
    >
      {{#if @centered}}
        {{! Centered layout: large icon centered on top }}
        <div
          class="flex h-14 w-14 items-center justify-center rounded-xl
            {{colors.iconBg}}
            mx-auto mb-4"
        >
          {{#let @icon as |Icon|}}
            <Icon @size={{28}} class={{colors.iconText}} />
          {{/let}}
        </div>
      {{else}}
        {{! Default layout: icon badge + title inline }}
        <div class="flex items-center gap-3 mb-4">
          <div
            class="flex h-10 w-10 items-center justify-center rounded-lg {{colors.iconBg}} shrink-0"
          >
            {{#let @icon as |Icon|}}
              <Icon @size={{20}} class={{colors.iconText}} />
            {{/let}}
          </div>
          <Heading data-test-feature-card-title @level={{4}} class="font-semibold text-gray-900">
            {{@title}}
          </Heading>
        </div>
      {{/if}}

      {{#if @centered}}
        <Heading data-test-feature-card-title @level={{4}} class="font-semibold text-gray-900 mb-2">
          {{@title}}
        </Heading>
      {{/if}}

      {{#if @description}}
        <Text
          data-test-feature-card-description
          class="text-gray-600 text-sm leading-relaxed {{if @centered 'mb-4'}}"
        >
          {{@description}}
        </Text>
      {{/if}}

      {{#if (has-block "action")}}
        <div data-test-feature-card-action class="{{unless @centered 'mt-4'}}">
          {{yield to="action"}}
        </div>
      {{/if}}
    </Card>
  {{/let}}
</template>;

export default FeatureCard;

export type { FeatureCardSignature };
